import { useEffect, useRef, useState } from "react";
import { MAX_QUANTIDADE_DO_SERVICO, quantidadePadrao } from "@bookdental/schemas";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { formatPriceCents } from "@/components/services/service-meta";
import { SingleSelectButtons } from "./SingleSelectButtons";
import { ServicosAgrupados } from "./ServicosAgrupados";
import { ShadeSelector } from "./ShadeSelector";
import { toggleSelection, type ServiceDraft } from "./prosthesis-service-draft";
import type { ServicoInicial } from "./NewOrderStartDialog";

const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";


type Props = {
  teeth: string;
  labOrganizationId?: string;
  onAdd: (draft: ServiceDraft) => void;
  onFullArchSelected: () => void;
  onSingleToothServiceSelected: () => void;
  /** Serviço e material que já vêm escolhidos (o "Pedir" do catálogo público). */
  inicial?: ServicoInicial;
};

/** Serviço → Material → Cor → Observações → Arquivo, por serviço. O serviço é escolhido direto
 *  (sem passo de categoria) — a categoria é só uma propriedade herdada do serviço escolhido, usada
 *  pra saber se deve auto-selecionar (ou desfazer) a arcada completa no odontograma. */
export function ProsthesisSimpleServiceForm({
  teeth,
  labOrganizationId,
  onAdd,
  onFullArchSelected,
  onSingleToothServiceSelected,
  inicial,
}: Props) {
  const [serviceCategoryId, setServiceCategoryId] = useState<string | null>(inicial?.serviceCategoryId ?? null);
  const [materialOptionId, setMaterialOptionId] = useState<string | null>(inicial?.materialOptionId ?? null);
  const [shadeOptionId, setShadeOptionId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  /** Nulo = acompanha os dentes marcados (arcada = 1); a pessoa digita quando não for o caso. */
  const [quantidadeDigitada, setQuantidadeDigitada] = useState<number | null>(null);

  const categoriesQuery = trpc.prosthesis.catalog.serviceCategories.useQuery({ labOrganizationId });
  // Passa o material escolhido: o servidor devolve cada cor com `compativel`, e a grade avisa
  // em vez de esconder — com o catálogo meio preenchido, esconder faria o dentista achar que o
  // laboratório não trabalha com aquela cor.
  const shadesQuery = trpc.prosthesis.catalog.shadeOptions.useQuery({
    labOrganizationId,
    materialOptionId: materialOptionId ?? undefined,
  });
  // `labOrganizationId` é obrigatório aqui: o cardápio é de cada laboratório, e sem dizer de quem
  // a consulta cairia no da própria clínica — que não tem nenhum.
  const materialOptionsQuery = trpc.prosthesis.catalog.materialOptions.useQuery(
    { labOrganizationId, serviceCategoryId: serviceCategoryId ?? undefined },
    { enabled: !!serviceCategoryId && !!labOrganizationId },
  );
  // Preview do preço que o servidor vai calcular e congelar no envio do pedido — só existe
  // quando o laboratório é uma organização real conectada (sem isso não há tabela de preços).
  const priceQuery = trpc.prosthesis.catalog.getPrice.useQuery(
    {
      labOrganizationId: labOrganizationId ?? "",
      serviceCategoryId: serviceCategoryId ?? "",
      materialOptionId: materialOptionId ?? undefined,
    },
    { enabled: !!labOrganizationId && !!serviceCategoryId },
  );
  // Mesma regra do servidor: obrigatória quando o laboratório tem cor que serve pro material.
  const corObrigatoria = !!labOrganizationId && (shadesQuery.data ?? []).some((s: any) => s.compativel);
  const escolhido = (categoriesQuery.data ?? []).find((c: any) => c.id === serviceCategoryId) as { abrangencia?: string } | undefined;
  const quantidade = quantidadeDigitada ?? quantidadePadrao(teeth, escolhido?.abrangencia);
  const precoUnitario = priceQuery.data?.priceCents ?? null;

  // O serviço que veio escolhido marca a arcada como a escolha à mão faria — uma vez, quando o
  // catálogo carrega.
  const inicialAplicado = useRef(false);
  useEffect(() => {
    if (!inicial || inicialAplicado.current || !categoriesQuery.data) return;
    inicialAplicado.current = true;
    const service = categoriesQuery.data.find((c: any) => c.id === inicial.serviceCategoryId);
    if (!service) {
      setServiceCategoryId(null);
      setMaterialOptionId(null);
    } else if (service.abrangencia === "ARCADA") onFullArchSelected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesQuery.data]);

  function reset() {
    setServiceCategoryId(null);
    setMaterialOptionId(null);
    setShadeOptionId(null);
    setNotes("");
    setQuantidadeDigitada(null);
  }

  function handleSelectService(id: string) {
    const next = toggleSelection(serviceCategoryId, id);
    setServiceCategoryId(next);
    // Material/cor eram compatíveis com o serviço anterior — não fazem mais sentido garantidos aqui.
    setMaterialOptionId(null);
    setShadeOptionId(null);
    if (next) {
      const service = (categoriesQuery.data ?? []).find((c: any) => c.id === next);
      if (service?.abrangencia === "ARCADA") onFullArchSelected();
      else onSingleToothServiceSelected();
    }
  }

  function handleAdd() {
    if (!serviceCategoryId) {
      toast.error("Escolha o serviço.");
      return;
    }
    if (!teeth.trim()) {
      toast.error("Escolha o dente ou a região.");
      return;
    }
    if (corObrigatoria && !shadeOptionId) {
      toast.error("Escolha a cor do serviço.");
      return;
    }
    const category = (categoriesQuery.data ?? []).find((c: any) => c.id === serviceCategoryId);
    const material = (materialOptionsQuery.data ?? []).find((m: any) => m.id === materialOptionId);
    const shade = (shadesQuery.data ?? []).find((s: any) => s.id === shadeOptionId);

    onAdd({
      serviceCategoryId,
      serviceCategoryLabel: category?.label ?? "",
      materialOptionId,
      materialOptionLabel: material?.label ?? null,
      shadeOptionId,
      shadeOptionLabel: shade?.code ?? null,
      translucencyOptionId: null,
      translucencyOptionLabel: null,
      blockSizeId: null,
      blockSizeLabel: null,
      manufacturingProcessId: null,
      manufacturingProcessLabel: null,
      characterizationOptionId: null,
      characterizationOptionLabel: null,
      finishingOptionId: null,
      finishingOptionLabel: null,
      teeth: teeth.trim(),
      priceCents: precoUnitario,
      quantidade,
      notes: notes.trim() || null,
      // Anexo agora é do pedido inteiro, não por serviço — ver ProsthesisOrderSummaryPanel.
      attachmentFileId: null,
      attachmentFileName: null,
    });
    reset();
  }

  return (
    <div className="space-y-4">
      <div>
        <label className={labelCls}>Serviço *</label>
        <ServicosAgrupados
          servicos={(categoriesQuery.data ?? []) as { id: string; label: string; group: string | null }[]}
          selectedId={serviceCategoryId}
          onSelect={handleSelectService}
        />
      </div>

      {serviceCategoryId && (
        <div>
          <label className={labelCls}>Material</label>
          <SingleSelectButtons
            options={(materialOptionsQuery.data ?? []).map((m: any) => ({ id: m.id, label: m.label }))}
            selectedId={materialOptionId}
            onSelect={(id) => setMaterialOptionId((prev) => toggleSelection(prev, id))}
          />
          {materialOptionsQuery.data?.length === 0 && (
            <p className="mt-1 text-[11px] text-zinc-400 dark:text-muted-foreground">
              Nenhum material compatível cadastrado pra esse serviço ainda.
            </p>
          )}
        </div>
      )}

      {serviceCategoryId && (
        <div>
          <label className={labelCls}>Cor{corObrigatoria ? " *" : ""}</label>
          <ShadeSelector
            shades={shadesQuery.data ?? []}
            selectedId={shadeOptionId}
            onSelect={(id) => setShadeOptionId((prev) => toggleSelection(prev, id))}
          />
        </div>
      )}

      {serviceCategoryId && (
        <div>
          <label className={labelCls}>Observações do Serviço</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Instruções específicas desse serviço..."
            className="w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
          />
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {serviceCategoryId && (
          <label className="flex items-center gap-1.5 text-[12.5px] text-zinc-500 dark:text-muted-foreground">
            Unidades
            <input
              type="number"
              min={1}
              max={MAX_QUANTIDADE_DO_SERVICO}
              value={quantidade}
              onChange={(e) => setQuantidadeDigitada(Math.min(Math.max(Number(e.target.value) || 1, 1), MAX_QUANTIDADE_DO_SERVICO))}
              aria-label="Unidades"
              className="h-8 w-14 rounded-md border border-zinc-200 bg-white px-2 text-sm tabular-nums text-zinc-900 dark:border-border dark:bg-card dark:text-foreground"
            />
          </label>
        )}
        {serviceCategoryId && labOrganizationId && (
          <span className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">
            Preço:{" "}
            {priceQuery.isLoading
              ? "..."
              : quantidade > 1 && precoUnitario != null
                ? `${quantidade} × ${formatPriceCents(precoUnitario)} = ${formatPriceCents(precoUnitario * quantidade)}`
                : formatPriceCents(precoUnitario)}
          </span>
        )}
        <button
          type="button"
          onClick={handleAdd}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
        >
          Adicionar serviço
        </button>
      </div>
    </div>
  );
}
